import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Brain, ChevronDown, Eye, Info, Loader, Shield, Sparkles, Users, Zap } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { TREND_REPORT_TYPE, getTrendGroups, getTrendSeries } from "../api/trends";
import { useAuth } from "../contexts/AuthContext";
import { computeMetricInsight, formatDate, labelForMetric } from "../utils/trends";

// Matches the labels used in the AI Audit report's Detailed Analysis / Overview
// tabs (src/components/DetailedAnalysisView.jsx, src/components/OverviewView.jsx),
// so a score means the same thing wherever the user sees it.
const AUDIT_METRIC_LABELS = {
  AEO: "AEO - Answer Engine",
  GEO: "GEO - Generative Engine",
  SXO: "SXO - Search Experience",
  AIO: "AIO - AI Overviews",
  AII: "AII - AI Integration",
  technical_score: "AI Crawlability",
};

const OTA_METRIC_LABELS = {
  direct_count: "Direct Count",
  ota_count: "OTA Count",
};

// Matches src/components/PerceptionTrends.jsx's curated attribute labels.
const PERCEPTION_METRIC_LABELS = {
  location: "Location",
  service: "Service",
  room_quality: "Room Quality",
  cleanliness: "Cleanliness",
  value_for_money: "Value for Money",
  dining: "Food & Dining",
  amenities: "Amenities",
  uniqueness: "Uniqueness / Character",
  family_friendliness: "Family-Friendliness",
  romantic_appeal: "Romantic Appeal",
  business_facilities: "Business Facilities",
  accessibility: "Accessibility",
  sustainability: "Sustainability",
};

// Mirrors the backend's `ReportType` values used by /trends/{report_type}[/groups].
// `groupLabel` is null for report types with no per-run dimension to group by —
// Persona, OTA vs Direct, and LLM Visibility are all property-level measures (how
// is *this business* doing overall), not something meant to be sliced by which
// query/persona surfaced it, except Persona, which is inherently per-persona.
// `showMetricSelector` is false for Persona — its mention_rate/fit_score are
// internal scoring inputs, not a dimension a user picks, so it only ever shows
// its composite visibility score. `scoreDomain` is the true min/max of the
// composite score's own scale; `metricsShareScoreDomain` is true when the other
// selectable metrics are on that same scale (Audit's category scores and
// Perception's attributes both share their type's 0-100 / 0-10 scale), false when
// they're a different unit entirely (OTA's raw link counts, Visibility's rank) and
// should get their own auto-scaled axis instead. There is no link to "the" report
// behind a trend: every trend — even one scoped to a single website/query/persona
// — aggregates points from many separate report generations over time, so no
// single report represents the whole chart.
//
// Persona and Perception here read the exact same persisted rollups (and the same
// server-computed `insight`) as the pre-existing standalone /persona/trends and
// /perception/trends views — the backend's /trends/{report_type} route is a thin
// second HTTP shape over PersonaTimeseriesHandler / PerceptionTimeseriesHandler,
// not a separate data store, so the two surfaces can't disagree with each other.
// Retiring the standalone views in favor of this screen is a follow-up, not done
// here, since they cover things this screen doesn't yet (fix-marker overlays,
// multi-persona overlay on one chart).
const REPORT_TYPES = [
  {
    value: TREND_REPORT_TYPE.AUDIT,
    label: "AI Audit",
    icon: Zap,
    groupLabel: "Website",
    showMetricSelector: true,
    metricSelectorLabel: "Category Score",
    compositeLabel: "Overall LLM SEO Score",
    metricLabels: AUDIT_METRIC_LABELS,
    scoreDomain: [0, 100],
    metricsShareScoreDomain: true,
    // Audit and LLM Visibility both support running a report before logging in.
    // A trend point can only be attributed to your account at the moment the
    // report is generated — one run anonymously and linked to your account
    // afterward still won't have a point for that specific run.
    coverageNote:
      "Only counts audits you were logged in for when they ran. An audit run anonymously and linked to your account afterward won't add a point for that run.",
  },
  {
    value: TREND_REPORT_TYPE.LLM_VISIBILITY,
    label: "LLM Visibility",
    icon: Eye,
    groupLabel: null,
    showMetricSelector: true,
    metricSelectorLabel: "Metric",
    compositeLabel: "Visibility Score",
    metricLabels: {},
    scoreDomain: [0, 100],
    metricsShareScoreDomain: false,
    // Unlike every other report type, a report here can legitimately have no
    // score at all — the AI response simply never mentioned the business for
    // that query. Surfaced to the user so a shorter-than-expected trend doesn't
    // read as a bug.
    coverageNote:
      "Only counts reports you were logged in for, where your business was actually mentioned by the AI. An anonymous report, or a query it wasn't mentioned in, doesn't add a point — so this trend can have fewer points than the number of reports you've run.",
  },
  {
    value: TREND_REPORT_TYPE.PERSONA,
    label: "Persona Visibility",
    icon: Users,
    groupLabel: "Persona",
    showMetricSelector: false,
    compositeLabel: "Visibility Score",
    metricLabels: {},
    scoreDomain: [0, 100],
    metricsShareScoreDomain: false,
  },
  {
    value: TREND_REPORT_TYPE.OTA_VS_DIRECT,
    label: "OTA vs Direct",
    icon: Shield,
    groupLabel: null,
    showMetricSelector: true,
    metricSelectorLabel: "Metric",
    compositeLabel: "Direct %",
    metricLabels: OTA_METRIC_LABELS,
    scoreDomain: [0, 100],
    metricsShareScoreDomain: false,
  },
  {
    value: TREND_REPORT_TYPE.PERCEPTION,
    label: "AI Perception",
    icon: Brain,
    groupLabel: null,
    showMetricSelector: true,
    metricSelectorLabel: "Attribute",
    compositeLabel: "Overall Score",
    metricLabels: PERCEPTION_METRIC_LABELS,
    scoreDomain: [0, 10],
    metricsShareScoreDomain: true,
  },
];
const TYPE_MAP = Object.fromEntries(REPORT_TYPES.map((t) => [t.value, t]));

const COMPOSITE = "__composite__";

const sharedAxisProps = {
  tick: { fill: "var(--color-on-surface-variant, #9aa0a6)", fontSize: 11 },
  tickLine: false,
  axisLine: false,
};
const sharedTooltipProps = {
  contentStyle: {
    background: "var(--color-surface-container-lowest, #1a1a2e)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    fontSize: 12,
  },
  labelStyle: { color: "var(--color-on-surface-variant, #9aa0a6)" },
};

const TypeSelector = ({ activeType, onSelect }) => (
  <div className="flex flex-wrap gap-2">
    {REPORT_TYPES.map((t) => {
      const Icon = t.icon;
      const isActive = t.value === activeType;
      return (
        <button
          key={t.value}
          onClick={() => onSelect(t.value)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
            isActive
              ? "bg-primary/10 border-primary text-primary"
              : "bg-surface-container-lowest border-outline-variant/30 text-on-surface-variant hover:border-primary/50"
          }`}
        >
          <Icon className="w-4 h-4" />
          {t.label}
        </button>
      );
    })}
  </div>
);

const SelectField = ({ id, label, value, onChange, disabled, children }) => (
  <div className="flex flex-col gap-1.5 min-w-[220px]">
    <label
      className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant"
      htmlFor={id}
    >
      {label}
    </label>
    <div className="relative">
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={onChange}
        className="w-full appearance-none bg-surface-container-lowest border border-outline-variant/40 rounded-xl text-sm pl-3.5 pr-9 py-2.5 text-on-surface shadow-sm transition-colors hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60 disabled:hover:border-outline-variant/40"
      >
        {children}
      </select>
      <ChevronDown className="w-4 h-4 text-on-surface-variant absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  </div>
);

const Trends = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialType = TYPE_MAP[searchParams.get("type")]
    ? searchParams.get("type")
    : REPORT_TYPES[0].value;
  const [reportType, setReportType] = useState(initialType);
  const [groupKey, setGroupKey] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [seriesData, setSeriesData] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(COMPOSITE);

  // A deep link (e.g. a report screen's "View Trends" button) arrives with a
  // group_key in the URL. Honored once, against whichever groups the initial
  // report type resolves to; every later type/group switch starts fresh.
  const requestedGroupKeyRef = useRef(searchParams.get("group_key") ?? "");

  const activeType = TYPE_MAP[reportType];
  const needsGroup = Boolean(activeType.groupLabel);

  // Fetch the group picker (websites / queries / personas) whenever the report
  // type changes. Clears the previous type's groups/selection synchronously so
  // the dropdown never shows a stale, wrong-type list while the new one loads.
  useEffect(() => {
    setSelectedMetric(COMPOSITE);
    setSeriesData(null);
    setGroupKey("");
    setGroups([]);
    if (!needsGroup) return undefined;

    const controller = new AbortController();
    setGroupsLoading(true);
    getTrendGroups(reportType, { signal: controller.signal })
      .then((r) => {
        const list = r.data?.groups ?? [];
        setGroups(list);
        const requested = requestedGroupKeyRef.current;
        requestedGroupKeyRef.current = "";
        const preferred = list.some((g) => g.group_key === requested)
          ? requested
          : (list[0]?.group_key ?? "");
        setGroupKey(preferred);
      })
      .catch(() => {
        if (!controller.signal.aborted) setGroups([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setGroupsLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, needsGroup]);

  // Fetch the series once we know which group (if any) to fetch it for.
  useEffect(() => {
    if (needsGroup && !groupKey) {
      setSeriesData(null);
      return undefined;
    }
    const controller = new AbortController();
    setSeriesLoading(true);
    getTrendSeries(reportType, needsGroup ? groupKey : undefined, { signal: controller.signal })
      .then((r) => setSeriesData(r.data))
      .catch(() => {
        if (!controller.signal.aborted) setSeriesData(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setSeriesLoading(false);
      });
    return () => controller.abort();
  }, [reportType, groupKey, needsGroup]);

  // Keep the URL in sync so a chosen report type/group is shareable and survives a refresh.
  useEffect(() => {
    const next = { type: reportType };
    if (needsGroup && groupKey) next.group_key = groupKey;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, groupKey, needsGroup]);

  const series = useMemo(() => seriesData?.series ?? [], [seriesData]);
  const metricIds = useMemo(() => {
    if (!activeType.showMetricSelector) return [];
    const ids = new Set();
    series.forEach((pt) => Object.keys(pt.metrics ?? {}).forEach((k) => ids.add(k)));
    return [...ids];
  }, [series, activeType.showMetricSelector]);

  const isComposite = !activeType.showMetricSelector || selectedMetric === COMPOSITE;
  // The x-axis position is the raw timestamp (a unique number), not the formatted
  // display string — multiple reports on the same day would otherwise share one
  // formatted label ("Sep 13, 2026") and recharts would stack them at the same
  // x-position, breaking the line. `tickFormatter`/`labelFormatter` below still
  // display the human-readable date; only the *positioning* uses the raw time.
  const chartData = series.map((pt) => ({
    date: new Date(pt.date).getTime(),
    value: isComposite ? pt.composite_score : (pt.metrics?.[selectedMetric] ?? null),
  }));
  const scoreDomain =
    isComposite || activeType.metricsShareScoreDomain ? activeType.scoreDomain : ["auto", "auto"];

  const selectedMetricLabel = isComposite
    ? activeType.compositeLabel
    : labelForMetric(selectedMetric, activeType.metricLabels);
  const insightText = isComposite
    ? seriesData?.insight
    : computeMetricInsight(series, selectedMetric, selectedMetricLabel);

  const selectedGroupLabel = needsGroup
    ? (groups.find((g) => g.group_key === groupKey)?.label ?? groupKey)
    : "";

  if (!user) {
    return (
      <section className="pt-12 pb-24 px-8 max-w-screen-md mx-auto text-center">
        <p className="text-on-surface-variant">Log in to view your trends.</p>
      </section>
    );
  }

  return (
    <div className="min-h-screen pt-10 pb-16 px-4 md:px-8">
      <div className="max-w-5xl mx-auto space-y-8">
        <div>
          <h1 className="font-headline text-3xl font-bold text-on-surface">Trends</h1>
          <p className="text-on-surface-variant mt-2">
            Track how your scores move over time across every report type.
          </p>
        </div>

        <TypeSelector activeType={reportType} onSelect={setReportType} />

        {activeType.coverageNote && (
          <div className="flex items-start gap-2.5 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 text-sm text-on-surface-variant">
            <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p>{activeType.coverageNote}</p>
          </div>
        )}

        {(needsGroup || activeType.showMetricSelector) && (
          <div className="flex flex-wrap items-end gap-4">
            {needsGroup && (
              <SelectField
                id="trends-group-select"
                label={activeType.groupLabel}
                value={groupKey}
                disabled={groupsLoading || groups.length === 0}
                onChange={(e) => {
                  setSelectedMetric(COMPOSITE);
                  setSeriesData(null);
                  setGroupKey(e.target.value);
                }}
              >
                {groups.length === 0 && (
                  <option value="">
                    {groupsLoading ? "Loading…" : `No ${activeType.groupLabel.toLowerCase()}s yet`}
                  </option>
                )}
                {groups.map((g) => (
                  <option key={g.group_key} value={g.group_key}>
                    {g.label}
                  </option>
                ))}
              </SelectField>
            )}

            {activeType.showMetricSelector && series.length > 0 && (
              <SelectField
                id="trends-metric-select"
                label={activeType.metricSelectorLabel}
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                <option value={COMPOSITE}>{activeType.compositeLabel}</option>
                {metricIds.map((id) => (
                  <option key={id} value={id}>
                    {labelForMetric(id, activeType.metricLabels)}
                  </option>
                ))}
              </SelectField>
            )}
          </div>
        )}

        {groupsLoading || seriesLoading ? (
          <div className="flex items-center justify-center py-20 gap-3">
            <Loader className="w-6 h-6 animate-spin text-primary" />
            <span className="text-on-surface-variant text-sm">Loading trends…</span>
          </div>
        ) : series.length === 0 ? (
          <div className="bg-surface-container-low rounded-2xl p-10 border border-surface-container-high text-center text-on-surface-variant text-sm space-y-1">
            <p>No trend data yet{selectedGroupLabel ? ` for "${selectedGroupLabel}"` : ""}.</p>
            <p className="text-xs text-on-surface-variant/70">
              Trends only record from reports generated after this feature launched — generate a new{" "}
              {activeType.label} report{needsGroup ? " for this one" : ""} to start its trend line.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-surface-container-low rounded-2xl p-6 border border-surface-container-high">
              <h3 className="font-headline text-base font-bold mb-4">
                {seriesData?.group_label ? `${seriesData.group_label} — ` : ""}
                {selectedMetricLabel} Over Time
              </h3>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="date"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={formatDate}
                    {...sharedAxisProps}
                  />
                  <YAxis domain={scoreDomain} {...sharedAxisProps} width={40} />
                  <Tooltip labelFormatter={formatDate} {...sharedTooltipProps} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name={selectedMetricLabel}
                    stroke="#2E5BFF"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#2E5BFF" }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {insightText && (
              <div className="bg-surface-container-low rounded-2xl p-5 border border-surface-container-high flex gap-3">
                <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-1">
                    Trend insight
                  </p>
                  <p className="text-sm text-on-surface">{insightText}</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Trends;
